import React, { FC, useEffect, useCallback, ChangeEvent, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { bool, number, object, string } from 'yup';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import Alert from '@material-ui/lab/Alert';
import Skeleton from '@material-ui/lab/Skeleton';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Switch, Tooltip, InputLabel, Select, MenuItem, OutlinedInput, FormControl } from '@material-ui/core';
import { Row } from 'components/UI/Row';
import { AppDispatch } from 'store/store';
import { History } from 'history';
import { getProduct, saveProduct, getProductFormLoaders, getProductFormApiErrors } from './productForm';

interface FormProps {
  batch_type: string;
  contact_id?: string;
  contact_product_code?: string;
  description: string;
  is_public: boolean;
  item_code?: string;
  name: string;
  qa_report_description: string;
  qa_report_title: string;
  serial_number_type: number;
  shop_url: string;
  web_url: string;
}

const FormSchema = object().shape({
  description: string(),
  item_code: string(),
  name: string().required('products.name is required'),
  is_public: bool().oneOf([true, false]),
  shop_url: string().url('products.shop_url format error'),
  web_url: string().url('products.web_url format error'),
  batch_type: string(),
  serial_number_type: number().oneOf([1, 4]),
  qa_report_title: string(),
  qa_report_description: string()
});

const batchTypes = [
  { title: 'products.batch type.none', value: 'disabled' },
  { title: 'products.batch type.weekly', value: 'week' },
  { title: 'products.batch type.monthly', value: 'month' },
  { title: 'products.batch type.every three months', value: 'three_months' },
  { title: 'products.batch type.every four months', value: 'four_months' },
  { title: 'products.batch type.year', value: 'year' }
];

const serialTypes = [
  { title: 'products.serial type.pseudo-incremental', value: 1 },
  { title: 'products.serial type.random', value: 4 }
];

const ProductDetails: FC<{}> = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const history: History = useHistory();

  const { handleSubmit, errors, setValue, control, setError } = useForm<FormProps>({
    mode: 'onBlur',
    resolver: yupResolver(FormSchema)
  });

  const { productId } = useParams<{ productId: string }>();
  const product = useSelector(getProduct);
  const [batch_type, setBatchType] = useState<string>(product?.batch_type || '');
  const [serial_number_type, setSerialType] = useState<number>(product?.serial_number_type || 4);
  const { isLoading, isSaving } = useSelector(getProductFormLoaders);
  const { loadingError, savingError } = useSelector(getProductFormApiErrors);

  useEffect(() => {
    if (product) {
      const {
        batch_type: batchType,
        item_code,
        name,
        description,
        shop_url,
        web_url,
        is_public,
        serial_number_type: serialNumberType,
        qa_report_title,
        qa_report_description,
        contacts_item_codes
      } = product;
      setValue('name', name);
      setValue('description', description);
      setValue('shop_url', shop_url);
      setValue('web_url', web_url);
      setValue('is_public', is_public);
      setValue('qa_report_title', qa_report_title);
      setValue('qa_report_description', qa_report_description);
      setValue('batch_type', batchType);
      setValue('item_code', item_code);
      setValue('serial_number_type', serialNumberType);
      setValue('contact_id', contacts_item_codes.length > 0 ? contacts_item_codes[0].contact_id : '');
      setValue('contact_product_code', contacts_item_codes.length > 0 ? contacts_item_codes[0].item_code ?? '' : '');
      setBatchType(batch_type);
      setSerialType(serial_number_type);
    }
  }, [product, setValue]);
  useEffect(() => {
    if (savingError) {
      setError('name', { type: 'required', message: 'products.name is required' });
    }
  }, [savingError, setError, t]);

  const onSubmit = useCallback(
    async (formData: FormProps) => {
      await dispatch(saveProduct({ product: formData, id: productId, history, nextTab: 1 }));
    },
    [dispatch, history, productId]
  );

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px', justifyContent: 'flex-end', display: 'flex' }}>
          <Skeleton variant="text" width="20%" height="19px" />
        </div>
      ) : (
        <Row margin="0 0 24px" justifyContent="flex-end" alignItems="center">
          <span>{t('products.public')}</span>
          <Controller
            render={({ onChange, value, ref }) => (
              <Tooltip title={t('products.public tooltip') || ''} arrow>
                <Switch
                  color="primary"
                  onChange={e => onChange(e.target.checked)}
                  checked={value}
                  inputRef={ref}
                  inputProps={{
                    'aria-label': 'is_public component'
                  }}
                />
              </Tooltip>
            )}
            control={control}
            defaultValue={false}
            name="is_public"
          />
        </Row>
      )}

      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px' }}>
          <Skeleton variant="text" width="100%" height="19px" />
        </div>
      ) : (
        <Row>
          <Controller
            as={
              <TextField
              helperText={t(get(errors, 'name.message'))}
              error={!isEmpty(errors.name)}
              label={t('products.name')}
              style={{ marginBottom: '16px', marginRight: '8px' }}
              variant="outlined"
              fullWidth
              multiline
              rows={1}
              />
            }
            control={control}
            defaultValue=""
            name="name"
            />
          <Controller
            as={
              <TextField
              helperText={t(get(errors, 'item_code.message'))}
              error={!isEmpty(errors.item_code)}
              label={t('products.item code')}
              style={{ marginBottom: '16px', marginLeft: '8px' }}
              variant="outlined"
              fullWidth
              multiline
              rows={1}
              />
            }
            control={control}
            defaultValue=""
            name="item_code"
            />
        </Row>
      )}

      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px' }}>
          <Skeleton variant="text" width="100%" height="19px" />
        </div>
      ) : (
        product &&
        product.contacts_item_codes.length > 0 && (
          <Row>
            <FormControl variant="outlined" fullWidth style={{ marginRight: '8px' }}>
              <InputLabel id="customer-label">{t('customers.customer')}</InputLabel>
              <Controller
                render={value => (
                  <Select
                    label={t('customers.customer')}
                    value={value.value}
                    style={{ marginBottom: '16px', marginRight: '8px' }}
                    fullWidth
                    onChange={(e: ChangeEvent<{ value: unknown }>) => {
                      const id = e.target.value as string;
                      const code = product.contacts_item_codes.find(v => v.contact_id === id);

                      setValue('contact_id', id);
                      setValue('contact_product_code', code?.item_code ?? '');
                    }}
                    input={<OutlinedInput label={t('customers.customer')} />}
                  >
                    {product.contacts_item_codes.map(({ contact_name, contact_id }) => (
                      <MenuItem key={contact_id} value={contact_id}>
                        {contact_name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                control={control}
                defaultValue=""
                name="contact_id"
              />
            </FormControl>
            <Controller
              render={value => (
                <TextField
                  label={t('orderList.customer item code')}
                  style={{ marginBottom: '16px', marginLeft: '8px' }}
                  variant="outlined"
                  fullWidth
                  onChange={e => setValue('contact_product_code', e.target.value)}
                  value={value.value}
                  rows={1}
                />
              )}
              control={control}
              defaultValue=""
              name="contact_product_code"
            />
          </Row>
        )
      )}

      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px', display: 'flex', flex: 1 }}>
          <Skeleton variant="rect" width="100%" height="100%" />
        </div>
      ) : (
        <Row>
          <FormControl variant="outlined" fullWidth style={{ marginRight: '8px' }}>
            <InputLabel id="batch-type-label">{t('products.batch type.type')}</InputLabel>

            <Controller
              defaultValue={batch_type}
              name="batch_type"
              control={control}
              as={
                <Select
                  label={t('products.batch type.type')}
                  value={batch_type}
                  style={{ marginBottom: '24px' }}
                  onChange={(e: ChangeEvent<{ value: unknown }>) => {
                    setBatchType(e.target.value as string);
                    setValue('batch_type', e.target.value as string);
                  }}
                  input={<OutlinedInput label={t('products.batch type.type')} />}
                >
                  {batchTypes.map(({ title, value }) => (
                    <MenuItem key={value} value={value}>
                      {t(title)}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
          </FormControl>

          <FormControl variant="outlined" fullWidth style={{ marginLeft: '8px' }}>
            <InputLabel id="serial-type-label">{t('products.serial type.type')}</InputLabel>

            <Controller
              defaultValue={serial_number_type}
              name="serial_number_type"
              control={control}
              as={
                <Select
                  label={t('products.serial type.type')}
                  value={batch_type}
                  style={{ marginBottom: '24px' }}
                  onChange={(e: ChangeEvent<{ value: unknown }>) => {
                    setSerialType(e.target.value as number);
                    setValue('serial_number_type', e.target.value as number);
                  }}
                  input={<OutlinedInput label={t('products.serial type.type')} />}
                >
                  {serialTypes.map(({ title, value }) => (
                    <MenuItem key={value} value={value}>
                      {t(title)}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
          </FormControl>
        </Row>
      )}

      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px', display: 'flex', flex: 1 }}>
          <Skeleton variant="rect" width="100%" height="100%" />
        </div>
      ) : (
        <Controller
          as={
            <TextField
              helperText={t(get(errors, 'description.message'))}
              error={!isEmpty(errors.description)}
              label={t('label.description')}
              style={{ marginBottom: '16px' }}
              variant="outlined"
              fullWidth
              multiline
              rows={10}
            />
          }
          control={control}
          defaultValue=""
          name="description"
        />
      )}

      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px' }}>
          <Skeleton variant="text" width="100%" height="19px" />
        </div>
      ) : (
        <>
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>{t('products.qa')}</div>
          <Controller
            as={
              <TextField
                helperText={t(get(errors, 'qa_report_title.message'))}
                error={!isEmpty(errors.qa_report_title)}
                label={t('label.name')}
                style={{ marginBottom: '16px' }}
                variant="outlined"
                fullWidth
                multiline
                rows={1}
              />
            }
            control={control}
            defaultValue=""
            name="qa_report_title"
          />
        </>
      )}

      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px', display: 'flex', flex: 1 }}>
          <Skeleton variant="rect" width="100%" height="100%" />
        </div>
      ) : (
        <Controller
          as={
            <TextField
              helperText={t(get(errors, 'qa_report_description.message'))}
              error={!isEmpty(errors.qa_report_description)}
              label={t('label.notes')}
              style={{ marginBottom: '16px' }}
              variant="outlined"
              fullWidth
              multiline
              rows={5}
            />
          }
          control={control}
          defaultValue=""
          name="qa_report_description"
        />
      )}

      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px' }}>
          <Skeleton variant="text" width="100%" height="19px" />
        </div>
      ) : (
        <>
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>{t('products.web links')}</div>
          <Controller
            as={
              <TextField
                helperText={t(get(errors, 'web_url.message'))}
                error={!isEmpty(errors.web_url)}
                label={t('products.web_url')}
                style={{ marginBottom: '16px' }}
                variant="outlined"
                fullWidth
                multiline
                rows={1}
              />
            }
            control={control}
            defaultValue=""
            name="web_url"
          />
        </>
      )}

      {isLoading ? (
        <div style={{ padding: '18.5px 0', borderRadius: '4px' }}>
          <Skeleton variant="text" width="100%" height="19px" />
        </div>
      ) : (
        <Controller
          as={
            <TextField
              helperText={t(get(errors, 'shop_url.message'))}
              error={!isEmpty(errors.shop_url)}
              label={t('products.shop_url')}
              style={{ marginBottom: '16px' }}
              variant="outlined"
              fullWidth
              multiline
              rows={1}
            />
          }
          control={control}
          defaultValue=""
          name="shop_url"
        />
      )}

      <div>
        <Row justifyContent="flex-end">
          <Button
            disabled={isSaving || isLoading}
            variant="contained"
            color="primary"
            type="submit"
            style={{ height: '44px', width: '128px' }}
          >
            {t('label.save')}
            {isSaving && (
              <CircularProgress
                color="primary"
                size={34}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: -17,
                  marginLeft: -17
                }}
              />
            )}
          </Button>
        </Row>
      </div>
    </form>
  );
};
export { ProductDetails };
